import { supabase } from '../lib/supabase';

interface CustomerInfo {
  name: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  booking: {
    dropOffDate?: string;
    dropOffTime?: string;
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
}

interface AutomationData {
  id: string;
  business_id: string;
  title: string;
  sms_action_active: boolean;
  sms_action_message: string;
  email_action_active: boolean;
  email_action_subject: string;
  email_action_body: string;
  notify_person_active: boolean;
  notify_person_target: 'all_resources' | 'admin' | 'custom';
  notify_person_phone: string;
  notify_person_message: string;
  wait_action_active: boolean;
  wait_action_duration: number;
  wait_action_unit: string;
  sms_action_order?: number | null;
  email_action_order?: number | null;
  notify_person_action_order?: number | null;
  wait_action_order?: number | null;
}

// Helper function to format time in AM/PM format
function formatTimeAMPM(time: string): string {
  if (!time) return '';
  
  // Parse the time string (assuming format like "14:30:00")
  const [hours, minutes] = time.split(':').map(Number);
  
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

// Get active automations with the scheduled trigger enabled for a business
async function getActiveScheduledAutomations(businessId: string): Promise<AutomationData[]> {
  console.log(`[SCHEDULED TRIGGER] Looking for scheduled automations for business: ${businessId}`);
  
  if (!supabase) {
    console.error('[SCHEDULED TRIGGER] Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('automation_settings')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .eq('scheduled_trigger_active', true); // This field should exist in your database

    if (error) {
      console.error('[SCHEDULED TRIGGER] Error fetching automations:', error);
      return [];
    }
    
    console.log(`[SCHEDULED TRIGGER] Found ${data?.length || 0} active scheduled automations`);
    
    return data || [];
  } catch (error) {
    console.error('[SCHEDULED TRIGGER] Error fetching automations:', error);
    return [];
  }
}

// Execute scheduled trigger automations when a booking is created
export async function executeScheduledTriggers(businessId: string, customerInfo: CustomerInfo): Promise<void> {
  console.log(`[SCHEDULED TRIGGER] 🚀 Processing scheduled triggers for business: ${businessId}`);
  console.log(`[SCHEDULED TRIGGER] 📋 Customer: ${customerInfo.firstName} ${customerInfo.lastName}`);
  
  if (!supabase) {
    console.error('[SCHEDULED TRIGGER] Supabase client not initialized');
    return;
  }

  try {
    // Get active automations with scheduled trigger for this business
    const automations = await getActiveScheduledAutomations(businessId);
    
    if (automations.length === 0) {
      console.log(`[SCHEDULED TRIGGER] No active scheduled automations found for business ${businessId}`);
      return;
    }

    // Get business information
    const { data: business, error: businessError } = await supabase
      .from('businesses')
      .select('name')
      .eq('id', businessId)
      .single();

    if (businessError) {
      console.error('[SCHEDULED TRIGGER] Error fetching business info:', businessError);
    }

    // Prepare template data for SMS and email messages
    const templateData: Record<string, string> = {
      customer_first_name: customerInfo.firstName || '',
      customer_last_name: customerInfo.lastName || '',
      customer_full_name: customerInfo.name || '',
      drop_off_date_formatted: customerInfo.booking.dropOffDate 
        ? new Date(customerInfo.booking.dropOffDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      drop_off_time_formatted: formatTimeAMPM(customerInfo.booking.dropOffTime || ''),
      start_date_formatted: customerInfo.booking.startDate 
        ? new Date(customerInfo.booking.startDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      start_time_formatted: formatTimeAMPM(customerInfo.booking.startTime || ''),
      business_name: business?.name || '',
      phoneNumber: customerInfo.phone || '',
      email: customerInfo.email || '',
      drop_off_date: customerInfo.booking.dropOffDate || '',
      drop_off_time: customerInfo.booking.dropOffTime || '',
      appointment_date: customerInfo.booking.dropOffDate || customerInfo.booking.startDate || '',
      appointment_time: customerInfo.booking.dropOffTime || customerInfo.booking.startTime || '',
      service_name: 'Appointment' // Default value for scheduled triggers
    };

    // Import the actions for direct execution
    const { SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION, WAIT_ACTION } = await import('../lib/automations/actions');
    
    // Process each automation
    for (const automation of automations) {
      console.log(`[SCHEDULED TRIGGER] Processing automation: ${automation.id} (${automation.title})`);
      
      // Collect all active actions with their order information
      const actionsToExecute: Array<{ action: any, order: number, type: string }> = [];
      
      // Check for Wait action
      if (automation.wait_action_active) {
        console.log(`[SCHEDULED TRIGGER] Found Wait action - order: ${automation.wait_action_order}`);
        actionsToExecute.push({
          action: {
            ...WAIT_ACTION,
            settings: {
              ...WAIT_ACTION.settings,
              waitDuration: automation.wait_action_duration || 30,
              waitUnit: (automation.wait_action_unit as 'minutes' | 'hours' | 'days') || 'minutes'
            }
          },
          order: automation.wait_action_order || 999,
          type: 'wait'
        });
      }
      
      // Check for SMS action
      if (automation.sms_action_active && customerInfo.phone) {
        console.log(`[SCHEDULED TRIGGER] Found SMS action - order: ${automation.sms_action_order}`);
        actionsToExecute.push({
          action: {
            ...SMS_ACTION,
            settings: {
              ...SMS_ACTION.settings,
              message: automation.sms_action_message || ''
            }
          },
          order: automation.sms_action_order || 999,
          type: 'sms'
        });
      }
      
      // Check for Email action
      if (automation.email_action_active && customerInfo.email) {
        console.log(`[SCHEDULED TRIGGER] Found Email action - order: ${automation.email_action_order}`);
        actionsToExecute.push({
          action: {
            ...EMAIL_ACTION,
            settings: {
              ...EMAIL_ACTION.settings,
              subject: automation.email_action_subject || '',
              emailBody: automation.email_action_body || ''
            }
          },
          order: automation.email_action_order || 999,
          type: 'email'
        });
      }
      
      // Check for Notify Person action
      if (automation.notify_person_active) {
        console.log(`[SCHEDULED TRIGGER] Found Notify Person action - order: ${automation.notify_person_action_order}`);
        actionsToExecute.push({
          action: {
            ...NOTIFY_PERSON_ACTION,
            settings: {
              ...NOTIFY_PERSON_ACTION.settings,
              notifyTarget: automation.notify_person_target || 'admin',
              notifyPhoneNumber: automation.notify_person_phone || '',
              notifyMessage: automation.notify_person_message || ''
            }
          },
          order: automation.notify_person_action_order || 999,
          type: 'notify_person'
        });
      }
      
      // Sort actions by their order and execute them in sequence
      const sortedActions = actionsToExecute.sort((a, b) => a.order - b.order);
      
      console.log(`[SCHEDULED TRIGGER] 🔄 Executing ${sortedActions.length} actions in order:`, 
        sortedActions.map(a => `${a.type} (order: ${a.order})`).join(' → '));
      
      // ⚡ CRITICAL FIX: Execute actions SEQUENTIALLY, not in parallel
      // This ensures wait actions actually delay subsequent actions
      for (const { action, type } of sortedActions) {
        try {
          console.log(`[SCHEDULED TRIGGER] ⚡ Executing ${type} action...`);
          
          if (type === 'wait') {
            console.log(`[SCHEDULED TRIGGER] ⏱️ STARTING WAIT: ${action.settings.waitDuration} ${action.settings.waitUnit}`);
            
            // Execute the wait action and WAIT for it to complete
            await action.execute(businessId, templateData);
            
            console.log(`[SCHEDULED TRIGGER] ✅ WAIT COMPLETED - continuing to next action`);
          } else if (type === 'sms') {
            console.log(`[SCHEDULED TRIGGER] 📱 Sending SMS to: ${customerInfo.phone}`);
            
            // Execute the SMS action
            await action.execute(businessId, templateData);
            
            console.log(`[SCHEDULED TRIGGER] ✅ SMS sent successfully`);
          } else if (type === 'email') {
            console.log(`[SCHEDULED TRIGGER] 📧 Sending email to: ${customerInfo.email}`);
            
            // Execute the Email action
            await action.execute(businessId, templateData);
            
            console.log(`[SCHEDULED TRIGGER] ✅ Email sent successfully`);
          } else if (type === 'notify_person') {
            console.log(`[SCHEDULED TRIGGER] 🔔 Sending notification to ${action.settings.notifyTarget}`);
            
            // Execute the notify person action
            await action.execute(businessId, templateData);
            
            console.log(`[SCHEDULED TRIGGER] ✅ Notification sent successfully`);
          }
          
        } catch (error) {
          console.error(`[SCHEDULED TRIGGER] ❌ ${type} action failed:`, error);
        }
      }
    }
    
    console.log(`[SCHEDULED TRIGGER] ✅ All scheduled automations completed`);
    
  } catch (error) {
    console.error(`[SCHEDULED TRIGGER] ❌ Failed to process scheduled triggers:`, error);
  }
} 